import { calculateMonthlyPayment, formatCurrency } from '../utils/mortgageCalculations';
import { calculateEscrow } from '../utils/propertyDataService';

function InterestRateComparison({ loanData, calculatedData }) {
    const baseRate = loanData.interestRate;

    // Generate rates from -1% to +1% in 0.25% increments
    const rates = [];
    for (let i = -1; i <= 1; i += 0.25) {
        const rate = baseRate + i;
        if (rate > 0) {
            rates.push(rate);
        }
    }

    const comparisons = rates.map(rate => {
        const monthlyPI = calculateMonthlyPayment(
            calculatedData.loanAmount,
            rate,
            loanData.loanYears
        );

        // Use manual PMI override from calculatedData
        const monthlyPMI = calculatedData.pmi;

        // Calculate escrow without PMI (just property tax + insurance)
        const escrowWithoutPMI = calculatedData.escrow - monthlyPMI;

        // Two different totals:
        // 1. Total Loan Paid = P&I + PMI only
        const totalLoanPaid = (monthlyPI + monthlyPMI) * loanData.loanYears * 12;

        // 2. Total Paid = P&I + PMI + escrow (property tax + insurance)
        const totalMonthly = monthlyPI + monthlyPMI + escrowWithoutPMI;
        const totalPaid = totalMonthly * loanData.loanYears * 12;

        const totalInterest = (monthlyPI * loanData.loanYears * 12) - calculatedData.loanAmount;

        return {
            rate,
            monthlyPI,
            monthlyPMI,
            totalMonthly,
            totalPaid,
            totalLoanPaid,
            totalInterest,
            isBase: Math.abs(rate - baseRate) < 0.01
        };
    });

    return (
        <div className="card">
            <div className="card-header">
                <h3 className="card-title">Interest Rate Comparison</h3>
            </div>

            <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--spacing-md)' }}>
                Compare how different interest rates affect your monthly payment and total cost.
                Your current rate of <strong style={{ color: 'var(--primary-color)' }}>{baseRate.toFixed(3)}%</strong> is highlighted.
            </p>

            <div className="table-container">
                <table style={{ width: '100%' }}>
                    <thead>
                        <tr>
                            <th>Interest Rate</th>
                            <th>Monthly P&I</th>
                            <th>Total Monthly</th>
                            <th>Total Interest</th>
                            <th>Total Paid (No Escrow)</th>
                            <th>Total Paid (w/ Escrow)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {comparisons.map((comp, index) => (
                            <tr key={index} className={comp.isBase ? 'highlight' : ''}>
                                <td>
                                    <strong>{comp.rate.toFixed(3)}%</strong>
                                    {comp.isBase && <span className="badge badge-primary" style={{ marginLeft: '0.5rem' }}>Current</span>}
                                </td>
                                <td>{formatCurrency(comp.monthlyPI)}</td>
                                <td>{formatCurrency(comp.totalMonthly)}</td>
                                <td>{formatCurrency(comp.totalInterest)}</td>
                                <td>{formatCurrency(comp.totalLoanPaid)}</td>
                                <td>{formatCurrency(comp.totalPaid)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Visual Summary */}
            <div className="summary-grid" style={{ marginTop: 'var(--spacing-md)' }}>
                <div className="summary-card">
                    <div className="summary-label">Savings at {(baseRate - 0.5).toFixed(3)}%</div>
                    <div className="summary-value" style={{ fontSize: '1.25rem' }}>
                        {formatCurrency(
                            comparisons.find(c => c.isBase)?.totalInterest -
                            comparisons.find(c => Math.abs(c.rate - (baseRate - 0.5)) < 0.01)?.totalInterest || 0
                        )}
                    </div>
                    <div className="summary-subtext">Total interest saved over loan life</div>
                </div>

                <div className="summary-card">
                    <div className="summary-label">Extra Cost at {(baseRate + 0.5).toFixed(3)}%</div>
                    <div className="summary-value" style={{ fontSize: '1.25rem' }}>
                        {formatCurrency(
                            comparisons.find(c => Math.abs(c.rate - (baseRate + 0.5)) < 0.01)?.totalInterest -
                            comparisons.find(c => c.isBase)?.totalInterest || 0
                        )}
                    </div>
                    <div className="summary-subtext">Additional interest over loan life</div>
                </div>
            </div>
        </div>
    );
}

export default InterestRateComparison;
